import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouteReuseStrategy } from '@angular/router';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import {
  BandwidthCachedOptions,
  CallingType,
  EnvironmentType,
  MicrosoftCachedOptions,
  MicrosoftDialPlanGroup,
  MicrosoftDialPlanGroupField,
  PstnConnectivityType,
  ZpmPrivilege,
} from '../../../../shared/models/generated/ziroModels';
import { MsTeamsCachedOptionsResource } from '../../../resources/ms-teams-cached-options.resource';
import { combineLatest, Subscription } from 'rxjs';
import { isEqual } from 'lodash';
import { ZiroFormsUpdate } from '../../../../forms/ziro-forms-models';
import { AuthenticationContext } from '../../../../shared/contexts/authentication.context';
import { BandwidthCachedOptionsContext } from '../../../contexts/bandwidth-cached-options.context';
import { BandwidthSettingsContext } from '../../../../shared/contexts/bandwidth-settings.context';
import { Microsoft365LicensesViewResource } from '../../../../shared/resources/microsoft-365-licenses-view.resource';
import { MsDialPlanGroupsContext } from '../../../contexts/ms-dial-plan-groups.context';
import { MicrosoftTenantsStateContext } from '../../../../shared/contexts/microsoft-tenants-state.context';
import { MicrosoftTenantProxyFeatureSettingsContext } from '../../../contexts/microsoft-tenant-proxy-feature-settings.context';

@Component({
  selector: 'ziro-microsoft-dial-plan-group-management',
  templateUrl: './ms-dial-plan-group-management.component.html',
  providers: [MsTeamsCachedOptionsResource, MicrosoftTenantProxyFeatureSettingsContext],
  standalone: false,
})
export class MsDialPlanGroupManagementComponent implements OnInit, OnDestroy {
  private _authenticationContext = inject(AuthenticationContext);
  private _msTeamCachedOptionsResource = inject(MsTeamsCachedOptionsResource);
  private _bandwidthCachedOptionsContext = inject(BandwidthCachedOptionsContext);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _msDialPlanContext = inject(MsDialPlanGroupsContext);
  private _bandwidthSettingsContext = inject(BandwidthSettingsContext);
  private _routeReuseStrategy = inject(RouteReuseStrategy);
  private _microsoft365LicensesViewResource = inject(Microsoft365LicensesViewResource);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _microsoftTenantProxyFeatureSettingsContext = inject(MicrosoftTenantProxyFeatureSettingsContext);

  isLoading = true;
  allDialPlanGroups: MicrosoftDialPlanGroup[];
  dialPlanGroup: MicrosoftDialPlanGroup = {
    customInputs: [
      {
        name: MicrosoftDialPlanGroupField.ON_PREM_LINE_URI_EXTENSION,
        required: false,
        show: false,
        value: '',
      },
    ],
    customSelects: [
      {
        name: MicrosoftDialPlanGroupField.CALLING_POLICY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.TEAMS_CALL_HOLD_POLICY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.TEAMS_CALL_PARK_POLICY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.CALLING_LINE_IDENTITY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.TENANT_DIAL_PLAN,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.EMERGENCY_CALLING_POLICY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.EMERGENCY_CALL_ROUTING_POLICY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.ONLINE_VOICE_ROUTING_POLICY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.ONLINE_VOICEMAIL_POLICY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.IP_PHONE_POLICY,
        defaultOption: 'Global',
        possibleOptions: ['Global'],
        show: false,
        required: true,
      },
      {
        name: MicrosoftDialPlanGroupField.EMERGENCY_LOCATION,
        defaultOption: '',
        possibleOptions: [],
        show: false,
        required: false,
      },
    ],
    billingLocation: '',
    bandwidthEmergencyCallingSettings: null,
    dialPlanRangesJson: [{ start: '', end: '' }],
    id: null,
    name: '',
    callingType: CallingType.DID,
    pstnConnectivityType: PstnConnectivityType.DIRECT_ROUTING,
    carrierName: '',
    mainNumber: null,
    adWriteDownList: null,
    sharedCallingRoutingPolicy: '',
    enforcedNumbersUsageLocation: '',
    emailNotificationSettings: null,
    defaultUnansweredCallSettings: null,
  };
  microsoftCachedOptions: MicrosoftCachedOptions;
  bandwidthCachedOptions: BandwidthCachedOptions;
  usageLocations: string[];
  isSupportUser: boolean;
  isBandwidthConfigured: boolean;
  isGccHigh: boolean;
  onPremAdWriteDownEnabled: boolean;

  private _groupId: string;
  private _isCopy: boolean;
  private _tenantId: number;
  private _tenantName: string;
  private _subs = new Subscription();

  ngOnInit(): void {
    this._routeReuseStrategy.shouldReuseRoute = () => false;
    this._groupId = this._route.snapshot.params['groupId'];
    this._tenantId = Number(this._route.snapshot.params.tenantId);
    this._isCopy = this._router.url.endsWith('copy');

    const sub = combineLatest([
      this._msDialPlanContext.state$,
      this._msTeamCachedOptionsResource.getByTenantId(this._tenantId),
      this._authenticationContext.state$,
      this._bandwidthSettingsContext.state$,
      this._microsoftTenantsStateContext.fieldConfigState$,
      this._microsoft365LicensesViewResource.getByTenantId(this._tenantId),
    ]).subscribe(
      ([dialPlanGroups, microsoftCachedOptions, currentUser, bandwidthSettings, tenantSettings, ms365LicensesView]) => {
        this.microsoftCachedOptions = microsoftCachedOptions;
        this.usageLocations = ms365LicensesView.usageLocationsList;
        this.isSupportUser = this._authenticationContext.userHasPrivilege(
          currentUser,
          ZpmPrivilege.ZIRO_SUPPORT_SETTINGS,
        );
        this.allDialPlanGroups = dialPlanGroups;
        this.isBandwidthConfigured =
          this.isSupportUser && this._bandwidthSettingsContext.isBandwidthConfigured(bandwidthSettings);

        const currentTenant = tenantSettings.privilegedTenants.find((msTenant) => msTenant.tenantId === this._tenantId);
        this.isGccHigh = currentTenant?.environmentType === EnvironmentType.GCC_HIGH;
        this._tenantName = currentTenant?.tenantName || '';

        if (this._groupId != null) {
          const dialPlanGroup = this.allDialPlanGroups.find((group) => group.id === this._groupId);
          if (!dialPlanGroup) {
            if (this.isLoading) {
              this._router.navigate(['../new-group'], { relativeTo: this._route });
            }
            return;
          }
          if (this._isCopy) {
            this.dialPlanGroup = {
              ...dialPlanGroup,
              id: undefined,
              name: `${dialPlanGroup.name} (Copy)`,
              dialPlanRangesJson: [{ start: '', end: '' }],
              mainNumber: '',
              billingLocation: '',
              bandwidthEmergencyCallingSettings: null,
              customInputs: [
                {
                  name: MicrosoftDialPlanGroupField.ON_PREM_LINE_URI_EXTENSION,
                  required: false,
                  show: false,
                  value: '',
                },
              ],
            };
          } else {
            this.dialPlanGroup = dialPlanGroup;
          }
        }
        this._setBreadcrumbs(this.dialPlanGroup.name);
        this.isLoading = false;
      },
    );

    const proxyFeatureSettingsSub = this._microsoftTenantProxyFeatureSettingsContext.state$.subscribe(
      (proxySettings) => {
        this.onPremAdWriteDownEnabled = proxySettings.onPremAdWriteDownEnabled;
      },
    );

    const bandwidthCachedOptionsSub = this._bandwidthCachedOptionsContext.state$.subscribe((bandwidthCachedOptions) => {
      this.bandwidthCachedOptions = bandwidthCachedOptions;
    });

    this._subs.add(proxyFeatureSettingsSub);
    this._subs.add(bandwidthCachedOptionsSub);
    this._subs.add(sub);
  }

  private _setBreadcrumbs(label: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      {
        label: 'tkey;admin.tenants.title',
        routerLink: false,
      },
      {
        label: this._tenantName,
        url: `/admin/tenants/${this._tenantId}/app-registration`,
        routerLink: true,
      },
      {
        label: 'tkey;dialplanmanagement.title',
        url: `/admin/tenants/${this._tenantId}/dial-plan-management`,
        routerLink: true,
      },
      { label: label },
    ]);
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  onFormUpdate(data: ZiroFormsUpdate<MicrosoftDialPlanGroup>): void {
    if (!isEqual(data.new.name, data.old?.name)) {
      this._setBreadcrumbs(data.new.name);
    }
  }
}
